import React from 'react';
import Tooltip from './Tooltip';
import { cn } from '../../utils/cn';
import {
  buildEntryChips,
  ENTRY_SOURCE_FILLED_CLASSES,
  ENTRY_SOURCE_TEXT_CLASSES,
  type EntryChipDescriptor,
} from '../../utils/entrySource';

interface EntrySourceBadgeProps {
  source: string | null | undefined;
  /** Per-feature SL-side counter (covers SL_REENTRY/SL_RECOST/SL_REEXEC). */
  reEntryCount?: number | null;
  /** Per-feature TP-side counter. */
  tpReexecCount?: number | null;
  /** Leg's SL re-entry config — drives the "Re-Entry/Re-Cost/Re-Exec N/M" budget chip. */
  reEntryConfig?: { enabled: boolean; type: string; count: number } | null;
  /** Leg's TP re-execute config — drives the "TP-Exec N/M" budget chip. */
  reExecuteConfig?: { enabled: boolean; type: string; count: number } | null;
  /** `'filled'` renders pills with background (card-style); `'text'` renders inline coloured text (table-row-style). */
  variant?: 'filled' | 'text';
  size?: 'xs' | 'sm';
  /** When false, suppresses the hover tooltip. Defaults to true. */
  showTooltip?: boolean;
  className?: string;
}

/**
 * Uniform entry-source badge. Renders a variable-length chip cluster:
 *
 *  1. **Label-only source chip** when the row's `entry_source` is a non-budget
 *     mechanism — WAT / ORB / MANUAL_RETRY / MSLC / TSL. (SL/TP re-execute
 *     sources don't get a standalone chip; the budget chip below covers them
 *     via the `N` counter.)
 *  2. **Budget chip** `"Re-Entry|Re-Cost|Re-Exec N/M"` when the leg has
 *     `reEntryConfig.enabled`. Label + colour driven by `reEntryConfig.type`.
 *  3. **Budget chip** `"TP-Exec N/M"` when the leg has `reExecuteConfig.enabled`.
 *
 * `N` is the row-level counter (snapshot of attempts used by the time this row
 * was spawned); `M` is the configured cap. So the chip shows both the chain
 * history (via N) and the remaining budget for the leg.
 *
 * Returns `null` when nothing applies — pure SCHEDULED entry on a leg with no
 * re-entry/re-execute config gets no badge (default-path silence preserved).
 */
export const EntrySourceBadge: React.FC<EntrySourceBadgeProps> = ({
  source,
  reEntryCount,
  tpReexecCount,
  reEntryConfig,
  reExecuteConfig,
  variant = 'filled',
  size = 'sm',
  showTooltip = true,
  className,
}) => {
  const chips = buildEntryChips(source, reEntryCount, tpReexecCount, reEntryConfig, reExecuteConfig);
  if (chips.length === 0) return null;

  const tooltip = showTooltip ? chips.map((c) => c.tooltip).join('\n') : null;

  const renderChip = (chip: EntryChipDescriptor, key: string) =>
    variant === 'filled' ? (
      <span
        key={key}
        className={cn(
          'inline-flex items-center rounded font-bold uppercase tracking-wider whitespace-nowrap',
          size === 'xs' ? 'px-1.5 py-0.5 text-[9px]' : 'px-2 py-0.5 text-[10px]',
          ENTRY_SOURCE_FILLED_CLASSES[chip.variant],
        )}
      >
        {chip.label}
      </span>
    ) : (
      <span
        key={key}
        className={cn(
          'inline-flex items-center font-bold uppercase tracking-wider whitespace-nowrap',
          size === 'xs' ? 'text-[9px]' : 'text-[10px]',
          ENTRY_SOURCE_TEXT_CLASSES[chip.variant],
        )}
      >
        {chip.label}
      </span>
    );

  const content = (
    <span className={cn('inline-flex items-center gap-1', className)}>
      {chips.map((chip, i) => renderChip(chip, `chip-${i}`))}
    </span>
  );

  // portal escapes the StrategyPositionsModal's overflow-clip so the bubble
  // isn't truncated when the badge sits near the modal's edges.
  return tooltip ? <Tooltip text={tooltip} portal>{content}</Tooltip> : content;
};

export default EntrySourceBadge;
